import { useCallback, useEffect, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { setSelected } from '../../../redux/catalogueManagement/action'
import debounce from '../../../utils/debounce'
import {
  include,
  isArray,
  isEqual,
  length,
  notEqual,
  ternary,
  values,
} from '../../../utils/javascript'
import { hasOnlySpace } from '../../../utils/validation'
import {
  addBrandApi,
  getAllProductSeriesApi,
  getBrandListApi,
  searchBrandApi,
} from '../catalogue.api'
import { payloadType, tabKeys } from '../catalogue.description'

const parentSelector = ({
  showBrand,
  showProduct,
  showUnitType,
  showModel,
  isAllProductSeries,
}) => {
  const { t } = useTranslations()
  const { selector, dispatch } = useRedux()
  const catalogueState = selector(state => state?.catalogue?.selected)
  const {
    brandId,
    productSeriesId,
    unitType,
    modelId,
    selected,
    customId,
    ...restData
  } = {
    ...catalogueState,
  }
  const [tempSelected, setTempSelected] = useState({})
  const [popup, setPopup] = useState({ open: false, message: '' })
  const [isDisable, setIsDisable] = useState(false)
  const [isOpen, setIsOpen] = useState({})
  const [searchValues, setSearchValues] = useState({})
  const [addLoader, setAddLoader] = useState(false)
  const pageSize = 10

  useEffect(() => {
    initialOptions()
  }, [])

  useEffect(() => {
    if (brandId && showProduct && !productSeriesId) {
      getSelectOptions({
        type: tabKeys.productSeries,
        parentId: brandId,
        restData,
      })
    }
    if (productSeriesId && showModel && !modelId) {
      getSelectOptions({
        type: tabKeys.model,
        parentId: productSeriesId,
        restData,
      })
    }
  }, [brandId, productSeriesId])

  const initialOptions = () => {
    if (showBrand) {
      getSelectOptions({ type: tabKeys.brand })
    }
    if (brandId && showProduct) {
      getSelectOptions({
        type: tabKeys.productSeries,
        parentId: brandId,
      })
    }
    if (productSeriesId && showModel) {
      getSelectOptions({
        type: tabKeys.model,
        parentId: productSeriesId,
      })
    }
  }

  const getParams = ({ pageNo = 1, type, parentId }) => {
    let params = `${pageNo}?pageSize=${pageSize}&type=${payloadType[type]}`
    if (include([tabKeys.productSeries, tabKeys.model], type))
      params += `&parentId=${parentId}`
    return params
  }

  const getSelectOptions = async ({
    pageNo = 1,
    type,
    parentId,
    search,
    restData,
  }) => {
    dispatch(
      setSelected({
        ...restData,
        [type]: {
          ...restData?.[type],
          loader: true,
        },
      }),
    )
    const params = getParams({ type, parentId, pageNo })
    let resp
    if (search?.trim()) {
      resp = await searchBrandApi({ params: `${params}&name=${search}` })
    } else {
      if (isAllProductSeries && isEqual(tabKeys.productSeries, type)) {
        resp = await getAllProductSeriesApi({ params: `?parentId=${parentId}` })
      } else {
        resp = await getBrandListApi({ params })
      }
    }
    dispatch(
      setSelected({
        ...restData,
        [type]: {
          ...resp?.data,
          list: [
            ...((notEqual(pageNo, 1) && restData?.[type]?.list) || []),
            ...(resp?.data?.list || []),
          ],
          loader: false,
        },
      }),
    )
    setIsDisable(false)
  }

  const debounceGetOptions = useCallback(debounce(getSelectOptions, 500), [])

  const onScroll = debounce((e, type) => {
    if (isDisable) return
    const { target } = e
    const { pageNo, hasMore } = { ...restData[type] }
    if (
      hasMore &&
      isEqual(target.scrollHeight - target.scrollTop, target.clientHeight)
    ) {
      let parentId
      if (isEqual(tabKeys.productSeries, type)) parentId = brandId
      if (isEqual(tabKeys.model, type)) parentId = productSeriesId
      getSelectOptions({
        pageNo: pageNo + 1,
        type,
        parentId,
        search: searchValues?.[type],
        restData,
      })
    }
  }, 300)

  const handleSelectChange = ({
    brandId,
    productSeriesId,
    modelId,
    unitType,
    option,
    showConfirmation,
  }) => {
    if (showConfirmation) {
      setPopup({
        open: true,
        message: t('msg_AreYouSureWantToChangeSelection'),
      })
      return setTempSelected({
        brandId,
        productSeriesId,
        modelId,
        option,
      })
    }
    let newSelected = {}
    if (brandId) {
      newSelected = {
        brandId,
        productSeriesId: null,
        unitType: null,
        modelId: null,
        [tabKeys.productSeries]: null,
        [tabKeys.model]: null,
        selected: {
          brand: option,
          productSeries: null,
          model: null,
        },
      }
    } else if (productSeriesId) {
      newSelected = {
        productSeriesId,
        unitType: null,
        modelId: null,
        [tabKeys.model]: null,
        selected: {
          ...selected,
          productSeries: option,
          model: null,
        },
      }
    } else if (unitType) {
      //changed the product series id when unit type is changed
      const newProductSeriesId = restData?.[tabKeys.productSeries]?.list
        ?.filter(({ name }) => isEqual(name, selected?.productSeries?.label))
        ?.find(item => isEqual(unitType, item?.unitType))?.id
      newSelected = {
        ...(newProductSeriesId ? { productSeriesId: newProductSeriesId } : {}),
        unitType,
        modelId: null,
        selected: {
          ...selected,
          productSeries: {
            ...selected?.productSeries,
            ...(newProductSeriesId ? { value: newProductSeriesId } : {}),
          },
          model: null,
        },
      }
    } else if (modelId) {
      newSelected = {
        modelId,
        selected: {
          ...selected,
          model: option,
        },
      }
    }
    dispatch(setSelected(newSelected))
  }

  const handleClosePopup = () => setPopup({ open: false, message: '' })

  const onInputSearch = async (search, type) => {
    setIsDisable(true)
    setSearchValues({ ...searchValues, [type]: search })
    let parentId
    if (isEqual(tabKeys.productSeries, type)) parentId = brandId
    if (isEqual(tabKeys.model, type)) parentId = productSeriesId
    if (notEqual(tabKeys.brand, type) && !parentId) return setIsDisable(false)
    if (!hasOnlySpace(search)) {
      if (isEqual(tabKeys.productSeries, type) && isAllProductSeries) {
      } else {
        debounceGetOptions({ parentId, search, type, restData })
      }
    }
  }

  const onDropdownVisible = visible => {
    setIsOpen({ ...isOpen, ...visible })
  }

  const handleAddNewItem = async ({ key, tabKey, showConfirmation }) => {
    const search = searchValues?.[tabKey]?.trim()
    if (!search) return
    setAddLoader(true)
    let params = `?source=JOB&shortDescription= &longDescription= &name=${search}`
    if (include([tabKeys.productSeries, tabKeys.model], tabKey))
      params += `&brandId=${brandId}`
    if (include([tabKeys.unitType, tabKeys.model], tabKey))
      params += `&productSeriesId=${productSeriesId}`
    if (isEqual(tabKey, tabKeys.unitType)) params += `&type=UNIT_TYPE`
    const resp = await addBrandApi({ params })
    setAddLoader(false)
    const value = isEqual(tabKey, tabKeys.unitType) ? search : resp?.data?.id
    if (value) {
      handleSelectChange({
        [key]: value,
        option: { label: search, value },
        showConfirmation,
      })
    }
    // temporary solution
    setTimeout(() => {
      initialOptions()
    }, 350)
    setIsOpen({ ...isOpen, [tabKey]: false })
    setSearchValues({ ...searchValues, [tabKey]: '' })
    if (isEqual(tabKeys.model, tabKey))
      dispatch(setSelected({ customId: value }))
  }

  const groupByName = list => {
    return isArray(list)
      ? values(
          list.reduce((acc, item) => {
            if (!acc[item.name]) {
              acc[item.name] = {
                ...item,
                unitTypes: [],
                ...(isEqual(item.name, selected?.productSeries?.label) && {
                  id: selected?.productSeries?.value,
                }),
              }
            }
            acc[item.name].unitTypes.push(item.unitType)
            return acc
          }, {}),
        )
      : []
  }

  const productSeriesList = groupByName(restData?.[tabKeys.productSeries]?.list)
  const modifyForOptions = (selectedOption, type) => {
    const list =
      isAllProductSeries && isEqual(type, tabKeys.productSeries)
        ? productSeriesList?.filter(({ name }) =>
            name
              ?.toLowerCase()
              ?.includes((searchValues?.[type] || '')?.toLowerCase()),
          )
        : restData?.[type]?.list
    const newList = []
    const isSearchList = searchValues?.[type]?.trim()
    list?.forEach(({ name, id }) => {
      if (isEqual(selectedOption?.value, id) && !isSearchList) return
      newList.push({
        label: name,
        value: id,
      })
    })
    return ternary(
      selectedOption?.value && !isSearchList,
      [selectedOption, ...newList],
      newList,
    )
  }

  const brandOptions = modifyForOptions(selected?.brand, tabKeys.brand)
  const productOptions = modifyForOptions(
    selected?.productSeries,
    tabKeys.productSeries,
  )
  const unitTypeOptions = productSeriesList
    ?.find(item => isEqual(item?.id, productSeriesId))
    ?.unitTypes?.map(elem => ({ label: elem, value: elem }))
    ?.filter(({ label }) =>
      label
        ?.toLowerCase()
        ?.includes((searchValues?.[tabKeys.unitType] || '')?.toLowerCase()),
    )
  const modelOptions = modifyForOptions(selected?.model, tabKeys.model)
  const isCustomProductSeries =
    productSeriesId &&
    !length(
      productSeriesList?.find(item => isEqual(item?.id, productSeriesId))
        ?.unitTypes,
    )

  const selectAttributes = [
    {
      className: 'brand-select',
      hidden: !showBrand,
      placeholder: t('ctlg_SelectBrand'),
      searchPlaceholder: t('ctlg_SearchBrand'),
      key: 'brandId',
      tabKey: tabKeys.brand,
      value: brandId,
      options: brandOptions,
      loading: restData?.[tabKeys.brand]?.loader,
    },
    {
      className: 'product-select',
      hidden: !showProduct,
      placeholder: t('ctlg_SelectProductSeries'),
      searchPlaceholder: t('ctlg_SearchProductSeries'),
      key: 'productSeriesId',
      tabKey: tabKeys.productSeries,
      value: productSeriesId,
      options: productOptions,
      loading: restData?.[tabKeys.productSeries]?.loader,
      disableInput: !brandId,
    },
    {
      className: 'product-select',
      hidden: !showUnitType,
      placeholder: t('ctlg_SelectUnitType'),
      // searchPlaceholder: t('ctlg_SearchProductSeries'),
      key: 'unitType',
      tabKey: tabKeys.unitType,
      value: unitType,
      options: unitTypeOptions,
      loading: restData?.[tabKeys.unitType]?.loader,
      disableInput: !productSeriesId,
      disableAdd: !isCustomProductSeries,
    },
    {
      className: 'product-select',
      hidden: !showModel,
      placeholder: t('job_SelectModel'),
      searchPlaceholder: t('ctlg_SearchModel'),
      key: 'modelId',
      tabKey: tabKeys.model,
      value: modelId,
      options: modelOptions,
      loading: restData?.[tabKeys.model]?.loader,
      disableInput: !productSeriesId,
    },
  ]

  const loader = values(restData)?.some(value => value?.loader)

  return {
    t,
    popup,
    loader,
    isOpen,
    addLoader,
    tempSelected,
    searchValues,
    selectAttributes,
    onScroll,
    onInputSearch,
    handleClosePopup,
    handleAddNewItem,
    onDropdownVisible,
    handleSelectChange,
  }
}

export default parentSelector
